import React, { useCallback, useState } from 'react';
import validator from '@/constant/valitator';
import useAlert from '@/hooks/use-alert';
import { useDispatch } from 'react-redux';
import { actions } from '@/store';
import postResetPasswordAfterVerifyEmail from '@/fetches/user/auth/post-reset-password-after-verify-email';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Message from '@/constant/message';
import useGoto from '@/hooks/use-goto';
import { page } from '@/constant/path-user';
import { InputText } from '../common/input-text';
import { AuthTitle } from '../common/auth-title';
import { AnchorText } from '../common/anchor-text';
import { AuthButton } from '../common/auth-button';

const ConfirmButton = styled(AuthButton)`
  margin-top: 15px;
`;

/** (비밀번호 찾기) 새 비밀번호 입력 */
export const FindPasswordInputNewPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { alert } = useAlert();
  const goto = useGoto();

  const validatePassword = useCallback((value: string) => {
    const { err, args } = validator.checkPassword(value);
    if (err) {
      if (args?.value) {
        return t(err, { value: args.value }) as string;
      }
      if (args?.valueKeys) {
        return t(err, { value: args.valueKeys.map((v) => t(v)).join('\n') }) as string;
      }
      return t(err) as string;
    }
    return '';
  }, [t]);

  const validateConfirmPassword = useCallback((value: string) => {
    if (!value) {
      return Message.비밀번호확인란을입력해주세요;
    } if (password !== value) {
      return Message.입력한두비밀번호가다릅니다;
    }
    return '';
  }, [password]);

  const onChangePasswordInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setPassword(value);
    setPasswordError(validatePassword(value));
  }, [validatePassword]);

  const onChangeConfirmPasswordInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setConfirmPassword(value);
    setConfirmPasswordError(t(validateConfirmPassword(value)));
  }, [validateConfirmPassword, t]);

  const submit = useCallback(async () => {
    let err = '';
    if (!err) err = t(validateConfirmPassword(confirmPassword));
    if (!err) err = validatePassword(password);
    if (err) {
      await alert(err);
      return;
    }

    setLoading(true);
    const result = await postResetPasswordAfterVerifyEmail(password);
    if (result) {
      // await alert(t(Message.비밀번호변경이완료되었습니다로그인화면으로이동합니다));
      goto(page.CLASS);
      dispatch(actions.user.authModal.init());
    } else {
      await alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
    }
    setLoading(false);
  }, [t, validateConfirmPassword, confirmPassword, validatePassword, password, alert, goto, dispatch]);

  return (
    <>
      <AuthTitle>{t(Message.비밀번호재설정)}</AuthTitle>
      <AnchorText>{t(Message.새로운비밀번호를입력하여재설정을완료해주세요)}</AnchorText>
      <InputText
        type="password"
        title={t(Message.새비밀번호)}
        placeholder={t(Message.새비밀번호를입력하세요)}
        errorText={passwordError}
        value={password}
        onChange={onChangePasswordInput}
        disabled={loading}
        autoFocus
        autoComplete="false"
      />
      <InputText
        type="password"
        title={t(Message.새비밀번호확인)}
        placeholder={t(Message.새비밀번호를다시입력해주세요)}
        errorText={confirmPasswordError}
        value={confirmPassword}
        onChange={onChangeConfirmPasswordInput}
        disabled={loading}
        autoComplete="false"
      />
      <ConfirmButton type="button" onClick={submit} disabled={loading}>{t(Message.확인)}</ConfirmButton>
    </>
  );
};
